import * as React from "react";
import {Outlet} from "react-router";
import {useIntl} from "react-intl";
import {loadQuery} from "react-relay/hooks";
import BreadcrumbNavigation from "./navigation/BreadcrumbNavigation";
import SecondaryNavigation from "./navigation/SecondaryNavigation";
import AnalysisForecastAccountsViewQuery from "./__generated__/AnalysisForecastAccountsViewQuery.graphql";
import AnalysisForecastBwaViewQuery from "./__generated__/AnalysisForecastBwaViewQuery.graphql";
import AnalysisForecastPlannedItemsViewQuery from "./__generated__/AnalysisForecastPlannedItemsViewQuery.graphql";
import AnalysisSimulationViewCatalogQuery from "./__generated__/AnalysisSimulationViewCatalogQuery.graphql";
import AnalysisSimulationViewQuery from "./__generated__/AnalysisSimulationViewQuery.graphql";
import AnalysisSimulationViewScenariosQuery from "./__generated__/AnalysisSimulationViewScenariosQuery.graphql";
import AnalysisServerCostViewQuery from "./__generated__/AnalysisServerCostViewQuery.graphql";
import AnalysisOpenItemsViewQuery from "./__generated__/AnalysisOpenItemsViewQuery.graphql";
import {cutOffDateForYear, defaultCutOffYear} from "./AnalysisOpenItemsView";

type AnalysisViewProps = {
    prepared: any
};

/**
 * The first day (UTC) of the month that is `monthsAgo` months before the last completed month.
 * monthsAgo = 0 → last completed month.
 */
export function startDateForMonthsAgo(monthsAgo: number): Date {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1 - monthsAgo, 1));
}

export function periodForMonthsAgo(monthsAgo: number): { periodStart: string, periodEnd: string } {
    const start = startDateForMonthsAgo(monthsAgo);
    const end = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
    return {
        periodStart: start.toISOString().slice(0, 10),
        periodEnd: end.toISOString().slice(0, 10),
    };
}

const AnalysisView: React.FC<AnalysisViewProps> = (props) => {
    const intl = useIntl();

    const breadcrumbNavigationItems = [
        {label: intl.formatMessage({id: 'breadcrumb.analysis'}), to: '/analysis'},
    ];

    const secondaryNavigationItems = [
        {
            label: intl.formatMessage({id: 'tab.analysis.income'}),
            to: '/analysis/income',
        },
        {
            label: intl.formatMessage({id: 'tab.analysis.expenses'}),
            to: '/analysis/expenses',
        },
        {
            label: intl.formatMessage({id: 'tab.analysis.forecast'}),
            to: '/analysis/forecast',
        },
        {
            label: intl.formatMessage({id: 'tab.analysis.simulation'}),
            to: '/analysis/simulation',
        },
        {
            label: intl.formatMessage({id: 'tab.analysis.serverCost'}),
            to: '/analysis/servers',
        },
        {
            label: intl.formatMessage({id: 'tab.analysis.openItems'}),
            to: '/analysis/openitems',
        },
    ];

    return (
        <>
            <BreadcrumbNavigation items={breadcrumbNavigationItems}/>
            <div className="py-4 md:flex md:items-center md:justify-between">
                <div className="flex-1 min-w-0">
                    <div className="mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 gap-x-4 gap-y-8">
                        <div className="flex items-center space-x-5">
                            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{intl.formatMessage({id: 'view.analysis.headline'})}</h1>
                        </div>
                    </div>
                </div>
            </div>
            <SecondaryNavigation items={secondaryNavigationItems}/>
            <div className="lg:mt-6 mx-auto lg:px-8 grid grid-cols-1 gap-x-4 gap-y-8">
                <Outlet context={props.prepared}/>
            </div>
        </>
    );
};

export default AnalysisView;

function prepare_AnalysisView(_params: any, relayEnvironment: any) {
    return {
        bankAccountsQuery: loadQuery(
            relayEnvironment,
            AnalysisForecastAccountsViewQuery,
            {},
            {fetchPolicy: 'store-and-network'},
        ),
        bwaMonthsQuery: loadQuery(
            relayEnvironment,
            AnalysisForecastBwaViewQuery,
            {},
            {fetchPolicy: 'store-and-network'},
        ),
        plannedItemsQuery: loadQuery(
            relayEnvironment,
            AnalysisForecastPlannedItemsViewQuery,
            {},
            {fetchPolicy: 'store-and-network'},
        ),
        catalogQuery: loadQuery(
            relayEnvironment,
            AnalysisSimulationViewCatalogQuery,
            {},
            {fetchPolicy: 'store-and-network'},
        ),
        simulationQuery: loadQuery(
            relayEnvironment,
            AnalysisSimulationViewQuery,
            {...periodForMonthsAgo(0), overrides: []},
            {fetchPolicy: 'store-and-network'},
        ),
        scenariosQuery: loadQuery(
            relayEnvironment,
            AnalysisSimulationViewScenariosQuery,
            {},
            {fetchPolicy: 'store-and-network'},
        ),
        serverCostQuery: loadQuery(
            relayEnvironment,
            AnalysisServerCostViewQuery,
            {},
            {fetchPolicy: 'store-and-network'},
        ),
        openItemsQuery: loadQuery(
            relayEnvironment,
            AnalysisOpenItemsViewQuery,
            {cutOffDate: cutOffDateForYear(defaultCutOffYear()), invoiceYear: null},
            {fetchPolicy: 'store-and-network'},
        ),
    };
}

export {prepare_AnalysisView};
